import { useEffect } from "react";

import { useRouter } from "next/router";

import Cookies from "js-cookie";

import { useDisclosure } from "@/storybook-components";
import { useAppSelector, useAppDispatch } from "@/redux/store/hooks";
import useUser from "@/hooks/useUser";
import useAuthGate from "@/hooks/useAuthGate";
import ActionButton from "./action-button";

import { getVotingStatus } from "@/helpers/ideas";
import useUnverifiedModal from "@/hooks/useUnverfiedModal";
import {
  setTriggerPoint,
  setModalStatus
} from "@/redux/features/unverified-modal/slice";
import { setTriggerPointCookie } from "@/helpers/set-trigger-point-cookie";
import type { IContentParams } from "@/types/brainstormers";

import OpenPostDrawer from "../open-post-drawer";

const PostDrawer = ({
  params
}: {
  params?: Partial<IContentParams>;
}): JSX.Element => {
  const defaultCategoryId = params?.categoryId;
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { brainstormer } = useAppSelector((state) => state?.brainstormers);
  const { triggerPoint } = useAppSelector((state) => state.unverifiedModal);
  const cookieValues = Cookies.get("trigger_point");
  let cookieTriggerPoint;
  if (cookieValues) {
    cookieTriggerPoint = JSON.parse(cookieValues);
  }
  const {
    id: brainstormerId,
    allowUnverifiedParticipation,
    state: brainStormerState,
    votingStartAt,
    votingEndAt
  } = brainstormer;

  const {
    canContribute,
    visibilityMode,
    state: projectState,
    id: projectId
  } = useAppSelector((state) => state.project.project);

  const { isUnverfiedUserLoggedIn, isRegisteredUserLoggedIn } = useUser();
  const { isGuestParticipationEnabled, openGuestLoginModal } = useAuthGate();

  const guestParticipationAllowed =
    isGuestParticipationEnabled && allowUnverifiedParticipation;

  const newIdeaTriggerPayloadExtras = () => ({
    projectId: projectId != null ? String(projectId) : undefined,
    toolId: brainstormerId != null ? String(brainstormerId) : undefined
  });

  const isVotingAllowed =
    (votingStartAt &&
      votingEndAt &&
      getVotingStatus(votingStartAt, votingEndAt) === "active") ||
    getVotingStatus(votingStartAt, votingEndAt) === "ended";

  const routerPath = router.asPath;
  const { action } = router.query;
  const createPostDisclosure = useDisclosure();

  // to trigger the unverified modal
  useUnverifiedModal(allowUnverifiedParticipation, (isProceedAllowed) => {
    if (isProceedAllowed) {
      dispatch(
        setTriggerPoint({ ...triggerPoint, isTriggeredPointActive: false })
      );
      if (
        triggerPoint.triggeredSource === "new_idea" ||
        cookieTriggerPoint?.triggeredSource === "new_idea"
      ) {
        createPostDisclosure.onOpen();
        addQueryParam();
      }
      Cookies.remove("trigger_point");
    }
  });
  const addQueryParam = () => {
    router.query.action = "new_idea";
    router.replace(router, "", { shallow: true });
  };

  const removeQueryParam = () => {
    const [updatedPath] = routerPath.split("?");
    router.replace(updatedPath, undefined, { shallow: true });
  };

  const triggerModal = async () => {
    // Guest login: keep ?action=new_idea so the effect below opens the drawer after auth.
    if (!guestParticipationAllowed) {
      await removeQueryParam();
    }
    dispatch(setModalStatus("create"));
    if (guestParticipationAllowed) {
      const inactiveNewIdeaTrigger = {
        triggeredSource: "new_idea",
        isTriggeredPointActive: false,
        ...newIdeaTriggerPayloadExtras()
      };
      setTriggerPointCookie(inactiveNewIdeaTrigger);
      dispatch(setTriggerPoint(inactiveNewIdeaTrigger));
      openGuestLoginModal({ action: "new_idea" });
      return;
    }
    const activeNewIdeaTrigger = {
      triggeredSource: "new_idea",
      isTriggeredPointActive: true,
      ...newIdeaTriggerPayloadExtras()
    };
    setTriggerPointCookie(activeNewIdeaTrigger);
    dispatch(setTriggerPoint(activeNewIdeaTrigger));
  };

  useEffect(() => {
    if (action === "new_idea") {
      if (visibilityMode === "Panelled" && !canContribute) {
        removeQueryParam();
        return;
      }
      if (brainStormerState === "archived" || projectState === "archived") {
        removeQueryParam();
        return;
      }
      if (
        getVotingStatus(votingStartAt, votingEndAt) === "ended" ||
        getVotingStatus(votingStartAt, votingEndAt) === "active"
      ) {
        removeQueryParam();
        return;
      }
      if (!isUnverfiedUserLoggedIn && !isRegisteredUserLoggedIn) {
        triggerModal();
        return;
      }
      createPostDisclosure.onOpen();
    }
  }, [
    createPostDisclosure.onOpen,
    action,
    visibilityMode,
    canContribute,
    brainStormerState,
    projectState,
    votingStartAt,
    votingEndAt,
    isUnverfiedUserLoggedIn,
    isRegisteredUserLoggedIn,
    guestParticipationAllowed
  ]);

  if (projectState === "archived" || brainStormerState === "archived")
    return undefined;

  return (
    <>
      <ActionButton
        projectVisibility={visibilityMode}
        canContribute={canContribute}
        isVotingAllowed={isVotingAllowed}
        allowUnverifiedParticipation={allowUnverifiedParticipation}
        onOpenHandler={createPostDisclosure.onOpen}
      />
      {(isUnverfiedUserLoggedIn || isRegisteredUserLoggedIn) && (
        <OpenPostDrawer
          onOpen={createPostDisclosure.onOpen}
          open={createPostDisclosure.open}
          onClose={() => {
            dispatch(setModalStatus("none"));
            createPostDisclosure.onClose();
          }}
          type="create"
          isEditPost={false}
          defaultCategoryId={defaultCategoryId}
          refreshParams={params}
        />
      )}
    </>
  );
};

export default PostDrawer;
